import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiClock, FiSearch, FiChevronLeft, FiChevronRight, FiSliders } from 'react-icons/fi';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { membersApi, schedulesApi, teamsApi } from '../services/api';
import AgentScheduleModal from './AgentScheduleModal';

export default function TeamAgents() {
  const navigate = useNavigate();
  const { selectedPortal } = useHubSpotPortals();
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [agentsPerPage] = useState(25);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [orphanedMembers, setOrphanedMembers] = useState([]);
  const [orphanedError, setOrphanedError] = useState(null);
  const [activeTeamOwnerIds, setActiveTeamOwnerIds] = useState(new Set());
  const [showActiveTeamAgentsOnly, setShowActiveTeamAgentsOnly] = useState(true);
  const getOwnerId = (owner) => String(owner?.id || owner?.hs_owner_id || '');

  // Fetch agents data
  const fetchAgents = useCallback(async () => {
    if (!selectedPortal?.portal_id) {
      setError('No active portal selected.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      //const response = await membersApi.fetchOwners(selectedPortal.portal_id);
      const response = await membersApi.fetchHubspotOwners(selectedPortal.portal_id);
      const agentsData = response.owners || [];
      //console.log('************** Agents Data:', agentsData);
      setAgents(agentsData);

      setOrphanedError(null);
      try {
        const [membersResponse, teamsResponse] = await Promise.all([
          membersApi.fetchPortalMembers(selectedPortal.portal_id),
          teamsApi.fetchTeams(selectedPortal.portal_id),
        ]);
        const portalMembers = membersResponse.members || [];
        const portalTeams = teamsResponse.teams || [];
        const ownerIdsOnActiveTeams = new Set();
        const ownerIdSet = new Set(agentsData.map((owner) => getOwnerId(owner)).filter(Boolean));
        const seenOwnerIds = new Set();
        const orphaned = [];
        const activeTeams = portalTeams.filter(
          (team) => team?.team_active === true || Number(team?.team_active) === 1
        );

        portalMembers.forEach((member) => {
          const ownerId = member?.hs_owner_id ? String(member.hs_owner_id) : '';
          if (!ownerId || ownerIdSet.has(ownerId) || seenOwnerIds.has(ownerId)) return;
          seenOwnerIds.add(ownerId);
          orphaned.push({
            hs_owner_id: ownerId,
            first_name: member.first_name || '',
            last_name: member.last_name || '',
            email: member.email || '',
          });
        });

        // Build active owners from team detail members to avoid hs_team_id join mismatches.
        const activeTeamDetails = await Promise.all(
          activeTeams
            .map((team) => team?.id)
            .filter(Boolean)
            .map((teamId) => teamsApi.fetchTeamById(teamId))
        );

        activeTeamDetails.forEach((teamDetail) => {
          const members = teamDetail?.members || [];
          members.forEach((member) => {
            const ownerId = member?.hs_owner_id ? String(member.hs_owner_id) : '';
            const isMemberActive =
              member?.member_active == null ||
              member?.member_active === true ||
              Number(member?.member_active) === 1;
            if (!ownerId || !isMemberActive) return;
            ownerIdsOnActiveTeams.add(ownerId);
          });
        });

        setActiveTeamOwnerIds(ownerIdsOnActiveTeams);
        setOrphanedMembers(orphaned);
      } catch (memberErr) {
        console.error('Error fetching portal members:', memberErr);
        setActiveTeamOwnerIds(new Set());
        setOrphanedMembers([]);
        setOrphanedError('Unable to check for team members missing from HubSpot.');
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPortal?.portal_id]);

  useEffect(() => {
    if (selectedPortal?.portal_id) {
      fetchAgents();
    }
  }, [selectedPortal, fetchAgents]);

  // Filter and sort agents
  useEffect(() => {
    let filtered = agents.filter(agent => {
      const searchString = `${agent.firstName || ''} ${agent.lastName || ''} ${agent.email || ''}`.toLowerCase();
      if (!searchString.includes(searchTerm.toLowerCase())) return false;

      if (!showActiveTeamAgentsOnly) return true;
      return activeTeamOwnerIds.has(getOwnerId(agent));
    });

    // Sort agents
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim();
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        default:
          aValue = a[sortBy] || '';
          bValue = b[sortBy] || '';
      }
      
      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    setFilteredAgents(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [agents, searchTerm, sortBy, sortOrder, showActiveTeamAgentsOnly, activeTeamOwnerIds]);

  // Pagination
  const totalPages = Math.ceil(filteredAgents.length / agentsPerPage);
  const startIndex = (currentPage - 1) * agentsPerPage;
  const endIndex = startIndex + agentsPerPage;
  const paginatedAgents = filteredAgents.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleManageSchedule = (agent) => {
    setSelectedAgent(agent);
    setShowScheduleModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedAgent(null);
  };

  const handleSaveSchedule = async (scheduleData) => {
    if (!selectedAgent) return;

    console.log('************** Schedule Data:', JSON.stringify(scheduleData));
    
    try {
      await schedulesApi.updateAgentSchedule(selectedAgent.id, scheduleData,selectedPortal?.portal_id );
      alert('Schedule saved successfully!');
      handleCloseScheduleModal();
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    }
  };

  const handleExportSchedules = async () => {
    if (!selectedPortal?.portal_id) {
      setBulkError('Select a portal before exporting.');
      return;
    }

    setBulkError(null);

    try {
      const response = await schedulesApi.exportSchedulesCsv(selectedPortal.portal_id);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `schedules-${selectedPortal.portal_id}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setBulkError('Failed to export schedules. Please try again.');
    }
  };

  const handleImportSchedules = async (event) => {
    if (!selectedPortal?.portal_id) {
      setImportError('Select a portal before importing.');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportError(null);
    setImportResult(null);

    try {
      const csvText = await file.text();
      const result = await schedulesApi.importSchedulesCsv(selectedPortal.portal_id, csvText);
      setImportResult(result);
    } catch (err) {
      console.error('Import failed:', err);
      setImportError('Failed to import schedules. Please check the CSV and try again.');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const handleManageAttributes = (agent) => {
    // Navigate to agent attributes page
    navigate(`/agent-attributes/${agent.id}`);
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Agents</h1>
            {selectedPortal && (
              <p className="text-sm text-blue-700 mt-1">
                Showing agents for portal: 
                <span className="font-semibold"> {selectedPortal.hub_domain}</span> 
                <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {orphanedError && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-900">
          {orphanedError}
        </div>
      )}

      {orphanedMembers.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-900">
          <div className="font-semibold mb-1">
            {orphanedMembers.length} team member{orphanedMembers.length === 1 ? '' : 's'} are in Distributely teams but no longer exist in HubSpot.
          </div>
          <div className="text-xs text-yellow-800 mb-2">
            Remove them from teams to avoid routing to inactive owners.
          </div>
          <div className="text-xs text-yellow-800">
            {orphanedMembers.slice(0, 8).map((member) => {
              const name = `${member.first_name || ''} ${member.last_name || ''}`.trim();
              return name ? `${name} (${member.hs_owner_id})` : member.hs_owner_id;
            }).join(', ')}
            {orphanedMembers.length > 8 ? `, +${orphanedMembers.length - 8} more` : ''}
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => navigate('/teams')}
              className="inline-flex items-center px-3 py-2 bg-white border border-yellow-300 text-yellow-900 rounded-lg hover:border-yellow-400 transition-colors duration-200"
            >
              Review Teams
            </button>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showActiveTeamAgentsOnly}
            onChange={(e) => setShowActiveTeamAgentsOnly(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Agents on Active Teams only
        </label>
        <p className="text-xs text-gray-500">
          Shows owners currently assigned as active members on at least one active team.
        </p>
      </div>

      {/* Bulk Schedule Actions */}
      <div className="mb-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:border-blue-300 hover:text-blue-600 transition-colors duration-200"
            onClick={handleExportSchedules}
          >
            Export Schedules CSV
          </button>
          <label className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:border-blue-300 hover:text-blue-600 transition-colors duration-200 cursor-pointer">
            {importing ? 'Importing...' : 'Import CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImportSchedules}
              disabled={importing}
            />
          </label>
        </div>
        <p className="text-xs text-gray-500">
          Import replaces schedules and time off for any owner in the CSV. Duplicate blocks are ignored.
        </p>
        {bulkError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {bulkError}
          </div>
        )}
        {importResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            Import complete: {importResult.updated} updated, {importResult.failed} failed.
          </div>
        )}
        {importError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {importError}
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-500">Loading agents...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-8">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={fetchAgents}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Agents Table */}
      {!loading && !error && (
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {paginatedAgents.length} of {filteredAgents.length} agents
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name
                      {sortBy === 'name' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      {sortBy === 'email' && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedAgents.length > 0 ? (
                  paginatedAgents.map((agent) => (
                    <tr key={agent.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <FiUser className="w-4 h-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {agent.firstName} {agent.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{agent.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleManageSchedule(agent)}
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                            title="Manage Schedule"
                          >
                            <FiClock className="w-4 h-4 mr-1" />
                            Schedule
                          </button>
                          <button
                            onClick={() => handleManageAttributes(agent)}
                            className="text-indigo-600 hover:text-indigo-800 flex items-center"
                            title="Manage Attributes"
                          >
                            <FiSliders className="w-4 h-4 mr-1" />
                            Attributes
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        {searchTerm ? 'No agents match your search criteria' : 'No agents found'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAgents.length)} of {filteredAgents.length} results
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  
                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          currentPage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && selectedAgent && (
        <AgentScheduleModal
          agent={selectedAgent}
          onClose={handleCloseScheduleModal}
          onSave={handleSaveSchedule}
          portalId={selectedPortal?.portal_id}
        />
      )}
    </div>
  );
}
